"use client"

import * as React from "react"
import { Eye, EyeOff } from "lucide-react"
import { UIInput, type UIInputProps } from "./ui-input"
import { UIButton } from "../buttons/ui-button"
import { cn } from "../../lib/utils"

export interface UIInputSecretProps extends Omit<UIInputProps, "type"> {
  /** Aria label for the show toggle button. Defaults to "Show". */
  showLabel?: string
  /** Aria label for the hide toggle button. Defaults to "Hide". */
  hideLabel?: string
  /**
   * Controlled visibility — pair with `onVisibleChange`. Defaults to internal state.
   */
  visible?: boolean
  onVisibleChange?: (visible: boolean) => void
}

/**
 * Password / secret input with built-in show/hide toggle.
 * Otherwise behaves identically to UIInput (label/required/error/helperText supported).
 */
export const UIInputSecret = React.forwardRef<
  React.ComponentRef<typeof UIInput>,
  UIInputSecretProps
>(
  (
    {
      className,
      showLabel = "Show",
      hideLabel = "Hide",
      visible: visibleProp,
      onVisibleChange,
      ...props
    },
    ref,
  ) => {
    const [internalVisible, setInternalVisible] = React.useState(false)
    const isControlled = visibleProp !== undefined
    const visible = isControlled ? visibleProp : internalVisible

    const toggle = () => {
      const next = !visible
      if (!isControlled) setInternalVisible(next)
      onVisibleChange?.(next)
    }

    const Icon = visible ? EyeOff : Eye

    return (
      <div className="relative">
        <UIInput
          ref={ref}
          type={visible ? "text" : "password"}
          className={cn("pr-10", className)}
          {...props}
        />
        <UIButton
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 bottom-0 h-9 w-9 text-muted-foreground hover:text-foreground"
          onClick={toggle}
          aria-pressed={visible}
          aria-label={visible ? hideLabel : showLabel}
          tabIndex={-1}
        >
          <Icon className="h-4 w-4" />
        </UIButton>
      </div>
    )
  },
)

UIInputSecret.displayName = "UIInputSecret"
